/**
 * Branding context shape for Photo Vote Tool — Story 5.6.
 *
 * Source of truth:
 *   - Architecture §2.3 lines 208-215 (per-event branding overlay via a
 *     `<style>` block keyed by `data-event="<eventId>"`; two
 *     `:root[data-event=...][data-theme=...]` rules with the four
 *     `--brand-*` properties).
 *   - Story 5.6 AC line 1052 (locked field names: `eventId`,
 *     `brandPrimaryDark`, `brandPrimaryLight`, `brandOnPrimaryDark`,
 *     `brandOnPrimaryLight`, `brandSoft`, `brandStrong`, `logoUrl`).
 *   - AR-12 (epics.md line 123): CloudFront SPA + edge function for theme
 *     injection. Story 5.6 forward-pairs but does NOT depend on the edge
 *     function (Critical Scope Decision #7 in story spec — client-side
 *     injection of `data-event` only).
 *   - FR32 (prd.md line 684; epics.md line 54): per-event branding (logo
 *     + primary color; default fallback if branding fails to load). The
 *     FR32.3 fallback path is `branding === null` → consumers render with
 *     the neutral default theme tokens.
 *   - UX-DR2 (epics.md line 137): two-theme design with `?theme=` URL
 *     preview override on broadcast surfaces. Story 5.6's `data-event`
 *     attribute is independent of `data-theme`; both attributes coexist
 *     on `<html>` and combine via the CSS attribute combinator selector
 *     `:root[data-event=...][data-theme=...]`.
 *   - UX-DR6 (epics.md line 141): branding overlay with auto-derived
 *     `--brand-primary`, `--brand-on-primary`, `--brand-soft`,
 *     `--brand-strong`. Story 5.6 implements the frontend half (CSS
 *     injection); Story 6.3 implements the backend derivation half.
 *
 * The `Branding` field names match Story 5.6 AC line 1052 + Story 6.3
 * AC line 1146 (`deriveBrandTokens` return shape) verbatim. Story 8.1
 * AC line 1601 currently lists nested-short-name field shape (`brand:
 * { primaryDark, primaryLight, ... }`); that discrepancy is flagged in
 * deferred-work.md per Critical Scope Decision #9 + #20 — Story 8.1
 * resolves at implementation time.
 *
 * `BrandingContext` is created with `null` as the empty value (mirrors
 * Story 5.2's `ThemeContext` shape). Consumers go through
 * `useBranding()` (Story 5.6's `web/src/branding/useBranding.ts`).
 * Unlike Story 5.2's `useTheme()`, `useBranding()` does NOT throw when
 * called outside `<BrandingProvider>` — it returns `null`. Critical
 * Scope Decision #1 in the story spec documents the rationale: not
 * every surface in the app is event-scoped (admin home renders the
 * event LIST without an event selected), so a throwing hook would
 * force defensive `<BrandingProvider branding={null}>` wrappers around
 * every Story 5.4 component instance consumed by non-event surfaces.
 */
import { createContext } from 'react';

/**
 * Per-event branding shape produced by Story 6.3's `deriveBrandTokens`
 * algorithm at event-create time and consumed by every event-scoped
 * surface (admin event detail, voter UI, submitter UI, broadcast
 * surfaces). Field names are LOCKED at AC line 1052; do not rename
 * without a coordinated change across Stories 6.3 / 6.4 / 6.5 / 8.1
 * / 9.1 / 12.x.
 *
 * - `eventId`: Crockford-base32 ULID (26 chars). Generated server-side
 *   per Story 6.4 + architecture §2.3.
 * - `brandPrimaryDark` / `brandPrimaryLight`: 6-digit hex strings
 *   (`#RRGGBB`). Pair selected per active theme via the CSS attribute
 *   combinator `:root[data-event=...][data-theme=dark|light]`.
 * - `brandOnPrimaryDark` / `brandOnPrimaryLight`: 6-digit hex strings
 *   that meet AA contrast against the corresponding primary (Story 5.3
 *   `meetsAA` validates at event-create; Story 6.3 derives).
 * - `brandSoft` / `brandStrong`: 6-digit hex strings used for accents
 *   (UX §2.3 line 137). Theme-independent (used in both dark + light
 *   scopes per architecture §2.3 line 213).
 * - `logoUrl`: HTTPS URL to a logo image stored in S3 + served via
 *   CloudFront, OR `null` for events without a logo (architecture §2.3
 *   line 129 lists logo as optional input, max 5 MB per Story 16.2). Validation of
 *   URL shape lives at the API layer (Story 6.4); BrandingProvider only
 *   asserts an `https://` prefix as defense-in-depth.
 */
export interface Branding {
  eventId: string;
  brandPrimaryDark: string;
  brandPrimaryLight: string;
  brandOnPrimaryDark: string;
  brandOnPrimaryLight: string;
  brandSoft: string;
  brandStrong: string;
  logoUrl: string | null;
}

/**
 * The shape exposed via `<BrandingContext.Provider value={...}>` to
 * children of `<BrandingProvider>`. Three derived fields:
 *
 * - `branding`: the full Branding object, OR `null` when no event is
 *   active (FR32.3 fallback path) OR when validation failed and the
 *   provider mounted no overrides (Critical Scope Decision #3 fallback).
 * - `eventId`: convenience accessor for `branding?.eventId ?? null`.
 *   Avoids consumer boilerplate.
 * - `logoUrl`: convenience accessor for `branding?.logoUrl ?? null`.
 *   Surfaces that render the event logo (admin top bar per Story 7.1+,
 *   broadcast join per Story 12.2) read this directly.
 */
export interface BrandingContextValue {
  branding: Branding | null;
  eventId: string | null;
  logoUrl: string | null;
}

export const BrandingContext = createContext<BrandingContextValue | null>(null);
